import { DotLegalHeader, DotLegalMultiSelect, DotLegalSelect, DotLegalToolTip, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { alpha, Box, Button, Theme } from "@mui/material";
import { useIsOnMediumScreen, useIsOnSmallScreen } from "../../common/hooks/mediaHooks";
import Paper from "../../common/paper/Paper";
import { ReactComponent as CopyImage } from "../../core/icons/Copy icon.svg";
import { SurveyArea } from "../surveys/Surveys.types";
import { ReactComponent as CalculationImage } from "./calculatorImage.svg";
import PriceCalculationCollapse from "./priceCalculationCollapse/PriceCalculationCollapse";
import { PriceCalculatorProps } from "./PriceCalculator";
import { WatchPriceType } from "./PriceCalculator.types";
import { PriceBox } from "./TrademarkPriceCalculator";
import { useWatchPriceCalculator } from "./WatchPriceCalculator.hooks";

function WatchPriceCalculator(props: PriceCalculatorProps) {
    const isOnMediumScreen = useIsOnMediumScreen();
    const isOnSmallScreen = useIsOnSmallScreen();
    const snackbar = useDotLegalSnackbar();

    const { searchOptions, setSearchOptions, getCountryOptions, isLoading, data } = useWatchPriceCalculator();

    const getPriceWrapper = (text: string) => {
        return (
            <DotLegalHeader fontWeight={500} marginBottom={1} headerStyle="small">
                <Box sx={{ whiteSpace: "nowrap" }}>{text}</Box>
            </DotLegalHeader>
        );
    };

    const getPriceHeader = (text: string) => {
        return (
            <DotLegalHeader fontWeight={500} marginBottom={1} headerStyle="small">
                {text}
            </DotLegalHeader>
        );
    };

    function getCountryName(countryCode: string) {
        return getCountryOptions(SurveyArea.Watches).find((x) => x.id === countryCode)?.name ?? "";
    }

    function formatMissingCountryPrices() {
        let result = "";
        if (data!.countriesWithMissingPrices.length === 1) {
            result = getCountryName(data!.countriesWithMissingPrices[0]) ?? "";
        } else {
            const lastCountry = getCountryName(data!.countriesWithMissingPrices[data!.countriesWithMissingPrices.length - 1]);
            const firstCountries = data!.countriesWithMissingPrices
                .slice(0, -1)
                .map((x) => getCountryName(x))
                .join(", ");
            result = firstCountries + " and " + lastCountry;
        }

        return (
            <Box sx={{ alignSelf: "center" }}>
                <DotLegalHeader headerStyle="medium">No data exists for {result}</DotLegalHeader>
            </Box>
        );
    }

    return (
        <Box
            sx={(theme: Theme) => ({
                minHeight: `calc(100vh - ${theme.spacingAsNumber(theme.customSpacing.contentSpacing) * 2 + 55}px)`,
                display: "flex",
                flexDirection: "column",
            })}
        >
            <Paper
                style={{
                    minHeight: 116,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    position: "relative",
                    marginBottom: 32,
                }}
                background="smallPurpleWave"
            >
                <Box sx={{ marginLeft: 3 }}>
                    <DotLegalHeader headerStyle="small" marginBottom={0} fontWeight={500}>
                        The Price Calculator helps you estimate the cost of watches.
                    </DotLegalHeader>
                </Box>
                <Box sx={{ marginRight: isOnSmallScreen ? 0 : 10 }}>
                    <CalculationImage />
                </Box>
            </Paper>

            <Paper fullHeight style={{ flex: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <DotLegalHeader color="primary" headerStyle={"large"} marginTop={2}>
                        Mark information
                    </DotLegalHeader>
                    <DotLegalHeader color="primary" headerStyle={"small"}>
                        Please select which transaction you wish to estimate the cost of
                    </DotLegalHeader>

                    <Box
                        sx={{
                            width: isOnMediumScreen ? "100%" : "40%",
                            display: "flex",
                            flexDirection: "column",
                            "& .MuiAutocomplete-input": { width: "100% !Important" },
                        }}
                    >
                        <Box
                            sx={(theme) => ({
                                "& .MuiSvgIcon-root": {
                                    fill: theme.palette.primary.main,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: `${theme.palette.primary.main} !Important`,
                                },
                            })}
                        >
                            <DotLegalSelect
                                options={[
                                    { id: `${WatchPriceType.WordMark}`, name: "Word mark" },
                                    { id: `${WatchPriceType.DeviceMark}`, name: "Device mark" },
                                ]}
                                onChange={(value) => {
                                    let tempModel = { ...searchOptions };
                                    tempModel.typeOfTransaction = Number(value);
                                    setSearchOptions(tempModel);
                                }}
                                placeholder={"Type of transaction"}
                                label={"Type of transaction"}
                                noOptionsLabel={"No options"}
                                selectedItem={searchOptions.typeOfTransaction.toString()}
                                disableClearable
                            />

                            <DotLegalMultiSelect
                                onChange={(value) => {
                                    let tempModel = { ...searchOptions };
                                    tempModel.countries = value ?? [];
                                    setSearchOptions(tempModel);
                                }}
                                chosenOptions={searchOptions.countries}
                                label={"Country"}
                                placeholder={"Country"}
                                isLoading={isLoading}
                                options={getCountryOptions(SurveyArea.Watches)}
                                Limit={5}
                                noOptionsLabel={"No options"}
                            />
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", "& .MuiCheckbox-root": { marginLeft: "-12px" } }}>
                            <Box></Box>
                            <Button
                                variant="contained"
                                onClick={() =>
                                    setSearchOptions({
                                        typeOfTransaction: WatchPriceType.WordMark,
                                        countries: [],
                                    })
                                }
                                sx={(theme: Theme) => ({
                                    marginTop: "15px",
                                    alignSelf: "flex-end",
                                    boxShadow: "none",
                                    "&.MuiButton-root.Mui-disabled": { backgroundColor: alpha(theme.palette.primary.main, 0.5) },
                                })}
                            >
                                Clear
                            </Button>
                        </Box>

                        {data?.countriesWithMissingPrices && data.countriesWithMissingPrices.length > 0 && formatMissingCountryPrices()}
                        {data?.countryPrices && data.countryPrices.length > 0 && (
                            <Box sx={{ marginTop: 2, "& > *": { marginBottom: 2 } }}>
                                <PriceCalculationCollapse hasData={data !== undefined} isLoading={isLoading}>
                                    <Box>
                                        <Box sx={{ borderBottom: "1px solid", marginBottom: 2 }}>
                                            <PriceBox>
                                                <DotLegalHeader headerStyle={"medium"}>Grand Total (Excl. of BB fee)</DotLegalHeader>
                                                <DotLegalHeader headerStyle={"medium"}>{data?.grandTotal?.toDanishLocaleString()} DKK</DotLegalHeader>
                                                <DotLegalToolTip text={"Copy grand total"}>
                                                    <Box
                                                        sx={{ position: "absolute", right: 50, top: 18, "&:hover": { cursor: "pointer" } }}
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(data?.grandTotal?.toString() ?? "");
                                                            snackbar.show(
                                                                <Box>
                                                                    Grand Total of <strong>{data?.grandTotal?.toDanishLocaleString()}</strong> DKK was copied
                                                                </Box>,
                                                                "success"
                                                            );
                                                        }}
                                                    >
                                                        <CopyImage />
                                                    </Box>
                                                </DotLegalToolTip>
                                            </PriceBox>
                                        </Box>
                                        {data?.countryPrices.map((price) => {
                                            return (
                                                <PriceBox key={price.countryCode}>
                                                    {getPriceHeader(`${getCountryName(price.countryCode)}`)}
                                                    {getPriceWrapper(`${price.price} DKK`)}
                                                </PriceBox>
                                            );
                                        })}
                                    </Box>
                                </PriceCalculationCollapse>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

export default WatchPriceCalculator;
